// Bot Service for Consumer Protection Networks
// Handles automated chat responses and conversation flow

import {
  addDoc,
  collection,
  serverTimestamp,
  getDocs,
  query,
  where,
  limit,
} from "firebase/firestore";
import { db } from "./firebase";

// Bot configuration
const BOT_CONFIG = {
  id: "cpn-bot-system",
  name: "Admin",
  email: "admin@consumerprotectionnetworks.com",
  displayName: "Admin",
  photoURL:
    "https://eu.ui-avatars.com/api/?name=Admin&size=45&rounded=true&background=007bff&color=ffffff",
  isBot: true,
  isAdmin: true,
  createdAt: new Date(),
};

// Business hours configuration (Central Time)
const BUSINESS_HOURS = {
  start: 9, // 9 AM
  end: 17, // 5 PM
  timezone: "America/Chicago",
};

// Bot conversation flow
const BOT_RESPONSES = {
  greeting: {
    text: "👋 Hello! Welcome to Consumer Protection Networks. How can I help you today?\n\n[1] I'm an existing client and need an update.\n[2] I have a billing or payment question.\n[3] Something else.",
    options: [
      { id: "1", text: "I'm an existing client and need an update." },
      { id: "2", text: "I have a billing or payment question." },
      { id: "3", text: "Something else." },
    ],
  },

  existingClient: {
    text: "I can help you with updates!\n\n[a] Check the status of my case\n[b] Need to submit my required documents\n[c] Talk to a specialist",
    options: [
      { id: "a", text: "Check the status of my case" },
      { id: "b", text: "Need to submit my required documents" },
      { id: "c", text: "Talk to a specialist" },
    ],
  },

  caseStatus: {
    text: "📋 Case Status Update\n\nThe quickest way to get a direct update is to schedule a call with our Release Department:\n\n🗓️ Book Appointment:\n<a href='https://calendly.com/cpn-booking' target='_blank'>Schedule Now</a>\n\nAlternative Contact Methods:\n📧 Email: <a href='mailto:releasedepartment@cpnmail.com'>releasedepartment@cpnmail.com</a>\n📞 Phone: <a href='tel:+18662440077'>(866) 244-0077</a>",
  },

  submitDocuments: {
    text: "📄 Document Submission\n\nYou can submit your required documents by email:\n\n📧 Email: <a href='mailto:releasedepartment@cpnmail.com'>releasedepartment@cpnmail.com</a>\n\nConfirmation Recommended:\nTo confirm everything is received correctly, we recommend booking a quick call:\n\n🗓️ Book Call: <a href='https://calendly.com/cpn-booking' target='_blank'>Schedule Now</a>",
  },

  talkToSpecialist: {
    text: "👨‍💼 Talk to a Specialist\n\nGreat choice! Please pick a time that works best for you:\n\n🗓️ Book Appointment:\n<a href='https://calendly.com/cpn-booking' target='_blank'>Schedule Now</a>",
  },

  billing: {
    text: "💳 Billing & Payment Assistance\n\nFor billing and payment assistance, please contact our billing department:\n\n📧 Email: <a href='mailto:clientservices@consumerprotectionnetworks.com'>clientservices@consumerprotectionnetworks.com</a>\n📞 Phone: <a href='tel:+18666821706'>(866) 682-1706</a>",
  },

  somethingElse: {
    text: "💬 General Support\n\nThanks for letting me know! The best way to get connected with the right person is to book a call:\n\n🗓️ Schedule Call: <a href='https://calendly.com/cpn-booking' target='_blank'>Schedule Now</a>\n\n📧 Or Email Us: <a href='mailto:releasedepartment@cpnmail.com'>releasedepartment@cpnmail.com</a>",
  },

  afterHours: {
    text: "🌙 After Hours Support\n\nOur team is not available right now, but we'd love to help!\n\nSchedule a Call:\n🗓️ <a href='https://calendly.com/cpn-booking' target='_blank'>Schedule Now</a>\n\nOr Email Us:\n📧 Case Questions: <a href='mailto:releasedepartment@cpnmail.com'>releasedepartment@cpnmail.com</a>\n📧 Billing Questions: <a href='mailto:clientservices@consumerprotectionnetworks.com'>clientservices@consumerprotectionnetworks.com</a>",
  },

  welcome: {
    text: "👋 Welcome to Consumer Protection Networks\n\nHow can I help you today?\n\nPlease select an option:\n[1] I'm an existing client and need an update\n[2] I have a billing or payment question\n[3] Something else",
  },
};

// Keywords for automatic response detection
const KEYWORDS = {
  greeting: [
    "hello",
    "hi",
    "hey",
    "good morning",
    "good afternoon",
    "good evening",
  ],
  caseStatus: ["status", "case", "update", "progress", "where", "how long"],
  documents: ["document", "documents", "submit", "send", "upload", "paperwork"],
  billing: [
    "billing",
    "payment",
    "pay",
    "bill",
    "invoice",
    "charge",
    "cost",
    "fee",
  ],
  specialist: [
    "specialist",
    "talk",
    "speak",
    "call",
    "phone",
    "representative",
  ],
  appointment: ["appointment", "schedule", "book", "meeting", "time"],
};

// Check if current time is within business hours
function isBusinessHours() {
  const now = new Date();
  const centralTime = new Date(
    now.toLocaleString("en-US", { timeZone: BUSINESS_HOURS.timezone })
  );
  const hour = centralTime.getHours();
  const day = centralTime.getDay(); // 0 = Sunday, 6 = Saturday

  // Check if it's a weekday (Monday-Friday) and within business hours
  return (
    day >= 1 &&
    day <= 5 &&
    hour >= BUSINESS_HOURS.start &&
    hour < BUSINESS_HOURS.end
  );
}

// Analyze message content and determine appropriate response
function analyzeMessage(message) {
  const lowerMessage = message.toLowerCase();

  // Check for specific option selections
  if (
    lowerMessage.includes("[1]") ||
    lowerMessage.includes("existing client")
  ) {
    return "existingClient";
  }
  if (lowerMessage.includes("[2]") || lowerMessage.includes("billing")) {
    return "billing";
  }
  if (lowerMessage.includes("[3]") || lowerMessage.includes("something else")) {
    return "somethingElse";
  }
  if (lowerMessage.includes("[a]") || lowerMessage.includes("case status")) {
    return "caseStatus";
  }
  if (
    lowerMessage.includes("[b]") ||
    lowerMessage.includes("submit documents")
  ) {
    return "submitDocuments";
  }
  if (
    lowerMessage.includes("[c]") ||
    lowerMessage.includes("talk to specialist")
  ) {
    return "talkToSpecialist";
  }

  // Check for keywords
  for (const [category, keywords] of Object.entries(KEYWORDS)) {
    if (keywords.some((keyword) => lowerMessage.includes(keyword))) {
      switch (category) {
        case "greeting":
          return isBusinessHours() ? "greeting" : "afterHours";
        case "caseStatus":
          return "caseStatus";
        case "documents":
          return "submitDocuments";
        case "billing":
          return "billing";
        case "specialist":
        case "appointment":
          return "talkToSpecialist";
        default:
          return isBusinessHours() ? "greeting" : "afterHours";
      }
    }
  }

  // Default response based on business hours
  return isBusinessHours() ? "greeting" : "afterHours";
}

// Get bot response based on message analysis
function getBotResponse(message) {
  const responseType = analyzeMessage(message);
  return BOT_RESPONSES[responseType] || BOT_RESPONSES.greeting;
}

// Get admin user ID (using existing admin instead of creating bot user)
export async function ensureBotUser() {
  try {
    // Query for admin user
    const usersRef = collection(db, "users");
    const adminQuery = query(usersRef, where("isAdmin", "==", true), limit(1));
    const adminSnapshot = await getDocs(adminQuery);

    if (!adminSnapshot.empty) {
      const adminDoc = adminSnapshot.docs[0];
      return adminDoc.id;
    } else {
      // Fallback: create bot user if no admin found
      const botQuery = query(
        collection(db, "users"),
        where("email", "==", BOT_CONFIG.email),
        limit(1)
      );

      const botSnapshot = await getDocs(botQuery);

      if (!botSnapshot.empty) {
        return botSnapshot.docs[0].id;
      }

      // Create bot user if it doesn't exist
      const botDocRef = await addDoc(collection(db, "users"), {
        ...BOT_CONFIG,
        createdAt: serverTimestamp(),
      });

      return botDocRef.id;
    }
  } catch (error) {
    console.error("Error getting admin user:", error);
    throw error;
  }
}

// Send automated bot response
export async function sendBotResponse(userMessage, userId) {
  try {
    // Get the original admin user to send response from
    const usersRef = collection(db, "users");
    const adminQuery = query(usersRef, where("isAdmin", "==", true));
    const adminSnapshot = await getDocs(adminQuery);

    // Find the original admin user (not the bot)
    let originalAdminId = null;
    adminSnapshot.docs.forEach((doc) => {
      const userData = doc.data();
      if (!userData.isBot) {
        originalAdminId = doc.id;
      }
    });

    // Fallback to bot user if no original admin found
    if (!originalAdminId) {
      originalAdminId = await ensureBotUser();
    }

    const response = getBotResponse(userMessage);

    // Add bot response to messages collection
    const messageData = {
      sender: originalAdminId,
      receiver: userId,
      text: response.text,
      timestamp: serverTimestamp(),
      read: false,
      isBot: true,
      botOptions: response.options || null,
    };

    await addDoc(collection(db, "messages"), messageData);

    return response;
  } catch (error) {
    console.error("Error sending bot response:", error);
    throw error;
  }
}

// Get welcome message for new users
export function getWelcomeMessage() {
  return BOT_RESPONSES.welcome.text;
}

// Check if message should trigger bot response
export async function shouldBotRespond(message, currentUserId, selectedUserId) {
  try {
    // Get all admin users
    const usersRef = collection(db, "users");
    const adminQuery = query(usersRef, where("isAdmin", "==", true));
    const adminSnapshot = await getDocs(adminQuery);

    const adminIds = adminSnapshot.docs.map((doc) => doc.id);

    // Don't respond to admin's own messages
    if (adminIds.includes(currentUserId)) {
      return false;
    }

    // Only respond to messages sent to any admin (selectedUserId is an admin)
    if (adminIds.includes(selectedUserId)) {
      return true;
    }
    return false;
  } catch (error) {
    console.error("Error checking if bot should respond:", error);
    return false;
  }
}

export { BOT_CONFIG, BOT_RESPONSES };
